import { CommonModule } from '@angular/common';
import { Component, ComponentRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';

export interface ModalHostInstance {
  id: number;
  component: any;
  data?: Record<string, unknown>;
  callback?: (result?: unknown) => void;
}

@Component({
  selector: 'pkg-modal-host',
  standalone: true,
  imports: [CommonModule],
  template: `<ng-container #vc></ng-container>`,
})
export class ModalHostComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) modal!: ModalHostInstance;
  @Output() closeRequested = new EventEmitter<unknown>();

  @ViewChild('vc', { read: ViewContainerRef, static: true }) vc!: ViewContainerRef;

  private compRef?: ComponentRef<any>;
  private outSub?: Subscription;
  private closeSub?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['modal']) {
      this.ensureComponent();
      this.hydrateInputs();
      this.bindOutputs();
    }
  }

  ngOnDestroy(): void {
    this.outSub?.unsubscribe();
    this.closeSub?.unsubscribe();
    this.compRef?.destroy();
  }

  private ensureComponent(): void {
    if (!this.modal?.component) return;
    if (this.compRef) return;
    this.vc.clear();
    this.compRef = this.vc.createComponent(this.modal.component);
  }

  private getDeclaredInputs(): Set<string> {
    const cmp = (this.compRef?.componentType as { ɵcmp?: { inputs?: Record<string, string> | Map<string, string> } })?.ɵcmp;
    const inputs = cmp?.inputs;
    if (!inputs) return new Set();
    if (inputs instanceof Map) return new Set(inputs.keys());
    return new Set(Object.keys(inputs));
  }

  private hydrateInputs(): void {
    if (!this.compRef) return;
    const instance = this.compRef.instance as Record<string, unknown>;
    const dataWithClose = {
      ...(this.modal.data || {}),
      close: (result?: unknown) => this.closeRequested.emit(result),
    };
    const declared = this.getDeclaredInputs();

    if (declared.has('data')) {
      this.compRef.setInput('data', dataWithClose);
    } else if (declared.has('modalData')) {
      this.compRef.setInput('modalData', dataWithClose);
    } else {
      const hasDataSetter = !!Object.getOwnPropertyDescriptor(Object.getPrototypeOf(instance), 'data')?.set;
      if (hasDataSetter) {
        instance['data'] = dataWithClose;
      } else if (this.modal.data && typeof this.modal.data === 'object') {
        for (const [k, v] of Object.entries(this.modal.data)) {
          if (declared.has(k)) {
            this.compRef.setInput(k, v);
          } else if (k in instance) {
            instance[k] = v;
          }
        }
        if (!declared.has('close') && dataWithClose.close) {
          instance['close'] = dataWithClose.close;
        }
      }
    }

    this.compRef.changeDetectorRef.detectChanges();
  }

  private bindOutputs(): void {
    if (!this.compRef) return;

    this.outSub?.unsubscribe();
    this.closeSub?.unsubscribe();

    const instance: any = this.compRef.instance as any;
    const out: any = instance?.callback ?? instance?.callBack;
    const closeOut: any = instance?.closeModal;

    if (out && typeof out.subscribe === 'function') {
      this.outSub = out.subscribe((result: unknown) => {
        if (typeof this.modal?.callback === 'function') {
          try {
            this.modal.callback(result);
          } catch {
            // ignore callback errors
          }
        }
        this.closeRequested.emit(result);
      });
    }

    if (closeOut && typeof closeOut.subscribe === 'function') {
      this.closeSub = closeOut.subscribe((result: unknown) => {
        this.closeRequested.emit(result);
      });
    }
  }
}
